import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import TeacherVoicePlayer from "@/components/landing/TeacherVoicePlayer";
import { useCanonical } from "@/lib/useCanonical";
import { track } from "@/lib/analytics";
import { WHATSAPP_NUMBER, whatsappLink } from "@/lib/config";
import {
  Gift,
  Sparkles,
  Mail,
  BookOpen,
  MessageCircle,
  ExternalLink,
  Instagram,
} from "lucide-react";

const COPY = {
  pt: {
    tagline: "Prof Lionel · francês de Paris pra brasileiros",
    subtitle: "Escolhe abaixo por onde a gente começa 👇",
    links: [
      {
        id: "aula",
        Icon: Gift,
        title: "1ª aula GRÁTIS (50 min, no Zoom)",
        sub: "Sem cartão · resposta em 24 h",
        to: "/aula-gratis",
        primary: true,
      },
      {
        id: "teste",
        Icon: Sparkles,
        title: "Faça o teste de nível grátis",
        sub: "5 min · descubra seu A1/A2/B1/B2/C1",
        to: "/teste-de-nivel",
      },
      {
        id: "blog",
        Icon: BookOpen,
        title: "Blog · dicas semanais",
        sub: "Gramática, cultura, DELF, viagem",
        to: "/blog",
      },
      {
        id: "newsletter",
        Icon: Mail,
        title: "Newsletter semanal (grátis)",
        sub: "1 dica curta por semana no seu inbox",
        to: "/#newsletter",
      },
      {
        id: "solte",
        Icon: ExternalLink,
        title: "Blog irmão · solteseufrances.com.br",
        sub: "Mais artigos, exercícios e cultura",
        href: "https://solteseufrances.com.br",
        external: true,
      },
    ],
    whatsapp: "Fala comigo no WhatsApp",
    instagram: "@francescomlionel",
  },
  en: {
    tagline: "Prof Lionel · Parisian French for Brazilians",
    subtitle: "Pick where we start 👇",
    links: [
      { id: "aula", Icon: Gift, title: "Book your FREE 1st class (50 min, Zoom)", sub: "No card · reply within 24h", to: "/aula-gratis", primary: true },
      { id: "teste", Icon: Sparkles, title: "Take the free level test", sub: "5 min · find your A1→C1", to: "/teste-de-nivel" },
      { id: "blog", Icon: BookOpen, title: "Blog · weekly tips", sub: "Grammar, culture, DELF, travel", to: "/blog" },
      { id: "newsletter", Icon: Mail, title: "Weekly newsletter (free)", sub: "One short tip a week in your inbox", to: "/#newsletter" },
      { id: "solte", Icon: ExternalLink, title: "Sister blog · solteseufrances.com.br", sub: "More articles, drills, culture", href: "https://solteseufrances.com.br", external: true },
    ],
    whatsapp: "Message me on WhatsApp",
    instagram: "@francescomlionel",
  },
  fr: {
    tagline: "Prof Lionel · français de Paris pour Brésiliens",
    subtitle: "Choisis par où on commence 👇",
    links: [
      { id: "aula", Icon: Gift, title: "Réserver mon 1er cours GRATUIT (50 min, Zoom)", sub: "Sans carte · réponse sous 24 h", to: "/aula-gratis", primary: true },
      { id: "teste", Icon: Sparkles, title: "Faire le test de niveau gratuit", sub: "5 min · A1 → C1", to: "/teste-de-nivel" },
      { id: "blog", Icon: BookOpen, title: "Blog · astuces hebdo", sub: "Grammaire, culture, DELF, voyage", to: "/blog" },
      { id: "newsletter", Icon: Mail, title: "Newsletter hebdomadaire (gratuite)", sub: "Une astuce courte par semaine", to: "/#newsletter" },
      { id: "solte", Icon: ExternalLink, title: "Blog jumeau · solteseufrances.com.br", sub: "Plus d'articles, exercices, culture", href: "https://solteseufrances.com.br", external: true },
    ],
    whatsapp: "M'écrire sur WhatsApp",
    instagram: "@francescomlionel",
  },
};

const BioLink = () => {
  const { lang } = useLanguage();
  const t = COPY[lang] || COPY.pt;
  useCanonical("/l");

  useEffect(() => {
    document.title = "Prof Lionel · Todos os meus links · npdlbr.com.br";
    track("biolink_view", { language: lang });
  }, [lang]);

  const onLinkClick = (id) => {
    track("biolink_click", { link_id: id, language: lang });
  };

  return (
    <div
      data-testid="biolink-page"
      className="min-h-screen bg-gradient-to-b from-[#1e2a5a] via-[#243070] to-[#1e2a5a] text-[#f4f1ea]"
    >
      <main className="max-w-md mx-auto px-6 py-12 sm:py-16 flex flex-col items-center">
        {/* Avatar */}
        <div className="relative">
          <div className="h-28 w-28 rounded-full overflow-hidden border-4 border-[#f4f1ea]/20 shadow-2xl">
            <img
              src="https://customer-assets.emergentagent.com/job_french-for-all-1/artifacts/fqb0z62o_Ame%CC%81lioration%20de%20la%20qualite%CC%81%20photo.png"
              alt="Prof Lionel"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute -bottom-1 -right-1 h-8 w-8 rounded-full bg-[#d9381e] flex items-center justify-center border-2 border-[#1e2a5a] shadow-lg">
            <span className="text-lg" role="img" aria-label="Bandeira da França">🇫🇷</span>
          </div>
        </div>

        {/* Name + tagline */}
        <p className="mt-6 font-display text-2xl tracking-tight">Na Ponta Da Língua Brasil</p>
        <p className="mt-1 text-sm text-[#e5b3b3] italic">« M. Bonjour-Merci »</p>
        <p className="mt-4 text-sm text-[#f4f1ea]/85 text-center max-w-xs">{t.tagline}</p>
        <p className="mt-6 text-xs uppercase tracking-[0.22em] text-[#f4f1ea]/60">
          {t.subtitle}
        </p>

        {/* Voice intro — 20 s of Prof Lionel BEFORE the link list. Doubles trust. */}
        <div className="mt-8 w-full">
          <TeacherVoicePlayer
            lang={lang}
            variant="dark"
            eventContext="biolink"
          />
        </div>

        {/* Links */}
        <div className="mt-8 w-full space-y-3">
          {t.links.map((lk) => {
            const { Icon } = lk;
            const inner = (
              <div className="flex items-center gap-4">
                <span
                  className={`inline-flex h-10 w-10 items-center justify-center rounded-full shrink-0 ${
                    lk.primary
                      ? "bg-[#f4f1ea] text-[#d9381e]"
                      : "bg-[#f4f1ea]/15 text-[#f4f1ea]"
                  }`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                </span>
                <div className="text-left flex-1 min-w-0">
                  <p className="font-semibold text-sm sm:text-base">{lk.title}</p>
                  {lk.sub && (
                    <p className={`text-xs mt-0.5 truncate ${lk.primary ? "text-[#f4f1ea]/85" : "text-[#f4f1ea]/60"}`}>
                      {lk.sub}
                    </p>
                  )}
                </div>
              </div>
            );
            const baseCls = `block w-full rounded-2xl border transition-all hover:-translate-y-0.5 p-4 ${
              lk.primary
                ? "bg-[#d9381e] border-[#d9381e] shadow-[0_16px_40px_-16px_rgba(217,56,30,0.6)] hover:bg-[#c02f16]"
                : "bg-[#f4f1ea]/5 border-[#f4f1ea]/20 hover:bg-[#f4f1ea]/10 hover:border-[#f4f1ea]/40"
            }`;
            if (lk.external) {
              return (
                <a
                  key={lk.id}
                  href={lk.href}
                  target="_blank"
                  rel="noopener"
                  data-testid={`biolink-${lk.id}`}
                  onClick={() => onLinkClick(lk.id)}
                  className={baseCls}
                >
                  {inner}
                </a>
              );
            }
            return (
              <Link
                key={lk.id}
                to={lk.to}
                data-testid={`biolink-${lk.id}`}
                onClick={() => onLinkClick(lk.id)}
                className={baseCls}
              >
                {inner}
              </Link>
            );
          })}
        </div>

        {/* Secondary — social */}
        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener"
            data-testid="biolink-whatsapp"
            onClick={() => onLinkClick("whatsapp")}
            className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/25 bg-[#f4f1ea]/5 px-4 py-2 text-xs text-[#f4f1ea]/85 hover:border-[#f4f1ea]/60 hover:text-[#f4f1ea] transition-colors"
          >
            <MessageCircle className="h-3.5 w-3.5" />
            {t.whatsapp}
          </a>
          <a
            href="https://instagram.com/francescomlionel"
            target="_blank"
            rel="noopener"
            data-testid="biolink-instagram"
            onClick={() => onLinkClick("instagram")}
            className="inline-flex items-center gap-2 rounded-full border border-[#f4f1ea]/25 bg-[#f4f1ea]/5 px-4 py-2 text-xs text-[#f4f1ea]/85 hover:border-[#f4f1ea]/60 hover:text-[#f4f1ea] transition-colors"
          >
            <Instagram className="h-3.5 w-3.5" />
            {t.instagram}
          </a>
        </div>

        <p className="mt-10 text-[10px] uppercase tracking-[0.22em] text-[#f4f1ea]/40">
          npdlbr.com.br
        </p>
      </main>
    </div>
  );
};

export default BioLink;
